import { memo, useMemo } from 'react';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  CartesianGrid,
  XAxis,
  YAxis,
  Legend,
  Tooltip as ChartTooltip,
} from 'recharts';
import { Empty, Skeleton } from 'antd';
import { Panel } from './Panel';
import { useWidgetData } from './shared';
import type { AreaChartDashboardWidget } from '../../types/panelConfig';

interface Props {
  widget: AreaChartDashboardWidget;
  sourceId: number | null | undefined;
  parameters?: Record<string, string>;
}

const numberFmt = new Intl.NumberFormat('tr-TR', { maximumFractionDigits: 1 });

/** Rapor satırları → grafik satırları; sıra rapordaki gibi kalır, boş/metin değer 0 olur. */
export function alanSatirlari(
  data: Record<string, unknown>[],
  categoryField: string,
  series: { field: string }[],
): Record<string, string | number>[] {
  return data.map((r) => {
    const out: Record<string, string | number> = { category: String(r[categoryField] ?? '') };
    for (const s of series) out[s.field] = Number(r[s.field]) || 0;
    return out;
  });
}

/**
 * Çok serili alan grafiği (S308 hamam "Günün ritmi": erkek/bayan dolu kabin, 30 dk dilim).
 * Kategori sırası rapordaki gibi KORUNUR: saat ekseni SQL'de sıralanır, bileşen yeniden
 * sıralamaz ya da toplamaz (chart-bar'ın değere göre sıralaması burada yanlış olurdu).
 */
function AreaChartWidgetImpl({ widget, sourceId, parameters }: Props) {
  const { data, isLoading } = useWidgetData(sourceId, `chart-area-${widget.reportCode}`, parameters);
  const { title, subtitle, categoryField, series } = widget.config;

  const rows = useMemo(() => alanSatirlari(data ?? [], categoryField, series), [data, categoryField, series]);

  return (
    <div data-testid="chart-area-widget">
      <Panel title={title} subtitle={subtitle}>
        {isLoading ? (
          <Skeleton.Node active style={{ width: '100%', height: 240 }} />
        ) : rows.length === 0 ? (
          <Empty description="Veri yok" style={{ padding: 40 }} />
        ) : (
          <ResponsiveContainer width="100%" height={240}>
            <AreaChart data={rows} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="2 4" stroke="var(--color-border)" vertical={false} />
              <XAxis
                dataKey="category"
                tick={{ fontSize: 11, fill: 'var(--color-text-muted)' }}
                axisLine={{ stroke: 'var(--color-border)' }}
                tickLine={false}
                interval="preserveStartEnd"
                minTickGap={24}
              />
              <YAxis
                tick={{ fontSize: 11, fill: 'var(--color-text-muted)' }}
                axisLine={false}
                tickLine={false}
                allowDecimals={false}
                width={32}
              />
              <ChartTooltip
                formatter={(v) => numberFmt.format(Number(v))}
                contentStyle={{
                  borderRadius: 4,
                  border: '1px solid var(--color-border)',
                  fontSize: 12,
                  padding: '6px 10px',
                  boxShadow: '0 4px 16px var(--shadow-color-strong)',
                }}
                labelStyle={{ fontWeight: 600, color: 'var(--color-text-primary)' }}
              />
              <Legend iconType="square" wrapperStyle={{ fontSize: 12 }} />
              {series.map((s) => (
                <Area
                  key={s.field}
                  type="monotone"
                  dataKey={s.field}
                  name={s.label}
                  stroke={s.color}
                  fill={s.color}
                  fillOpacity={0.2}
                  strokeWidth={2}
                  isAnimationActive={false}
                />
              ))}
            </AreaChart>
          </ResponsiveContainer>
        )}
      </Panel>
    </div>
  );
}

export const AreaChartWidget = memo(AreaChartWidgetImpl);
